// Messages between the UI thread (db/client.ts) and the DB worker (db/worker.ts).
import type { PushedKey, SyncRecord } from './syncOps';

export type DbMode = 'real' | 'demo';
export type Row = Record<string, unknown>;
export type Bind = (string | number | null)[];

export type DbRequest =
  | { type: 'open'; mode: DbMode; userId?: string }
  | { type: 'destroy' }
  | { type: 'pendingChanges' }
  | { type: 'collectDirty'; limit: number }
  | { type: 'markPushed'; keys: PushedKey[] }
  | { type: 'applyRemote'; records: SyncRecord[] }
  | { type: 'query'; sql: string; bind?: Bind }
  | { type: 'insert'; table: string; values: Row }
  | { type: 'update'; table: string; id: string; patch: Row }
  | { type: 'upsert'; table: string; id: string; values: Row }
  | { type: 'softDelete'; table: string; id: string }
  | { type: 'getDeviceSetting'; key: string }
  | { type: 'setDeviceSetting'; key: string; value: string | null }
  | { type: 'setReadOnly'; readOnly: boolean }
  | { type: 'info' }
  | { type: 'putAttachment'; ownerTable: string; ownerId: string; mime: string; fileName: string | null; bytes: Uint8Array<ArrayBuffer> }
  | { type: 'readAttachment'; id: string }
  | { type: 'storeAttachment'; id: string; bytes: Uint8Array<ArrayBuffer> }
  | { type: 'deleteAttachment'; id: string }
  | { type: 'attachmentWork' }
  | { type: 'attachmentUploaded'; id: string }
  | { type: 'attachmentRemoved'; id: string }
  | { type: 'exportBackup' }
  | { type: 'openBackup'; bytes: Uint8Array<ArrayBuffer> }
  | { type: 'applyBackup'; files: BackupFile[] }
  | { type: 'discardBackup' };

/** An attachment file inside a backup, by its opfs_path. */
export interface BackupFile {
  name: string;
  bytes: Uint8Array<ArrayBuffer>;
}

export interface BackupExport {
  db: Uint8Array<ArrayBuffer>;
  schemaVersion: number;
  counts: Record<string, number>;
  files: BackupFile[];
  /** Attachments whose file isn't on this device, so they're missing from the backup. */
  missingFiles: number;
}

export interface BackupSummary {
  schemaVersion: number;
  counts: Record<string, number>;
}

/** Attachment files this device still has to upload, and deleted ones to remove from the account. */
export interface AttachmentWork {
  uploads: { id: string; mime: string }[];
  removals: { id: string; uploaded: boolean }[];
}

export type DbErrorCode = 'UNSUPPORTED' | 'READ_ONLY' | 'NOT_FOUND' | 'INVALID' | 'NOT_OPEN' | 'INTERNAL';

export interface DbInfo {
  mode: DbMode;
  file: string | null;
  sqliteVersion: string;
  vfs: string;
  schemaVersion: number;
  poolFiles: string[];
  poolCapacity: number | null;
  tables: { name: string; rows: number }[];
  readOnly: boolean;
}

export type WorkerMessage =
  | { kind: 'response'; id: number; ok: true; result: unknown }
  | { kind: 'response'; id: number; ok: false; error: { code: DbErrorCode; message: string } }
  /** `local`: written on this device (needs pushing). `remote`: arrived from a pull. */
  | { kind: 'tables-changed'; tables: string[]; source: 'local' | 'remote' };

export interface RequestEnvelope {
  id: number;
  request: DbRequest;
}

export class DbError extends Error {
  constructor(
    public readonly code: DbErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'DbError';
  }
}
